/**
 * EmployeeSyncPanel — pulpit synchronizacji PRACOWNIKÓW dla jednej integracji Symfonia FK.
 *
 * Odbicie `ContractorSyncPanel` na encji „pracownicy" (świadomie OSOBNY komponent, nie uogólnienie —
 * kontrahenci działają i nie chcemy ich ruszać). Różnice: pracownicy są FK-only, mają grupy ze
 * słownika `employee_groups`; wykluczenia to grupy + lista id (kategorii pracowników nie obejmują).
 *
 * Układ: statystyki + klucz dopasowania na górze, niżej ZAKŁADKI: Import / Eksport / Automatyzacja.
 * Import → `SyncEmployeesModal`, Eksport → `ExportEmployeesModal` (push-employees, FK-only), wykluczenia
 * → `erp_integrations.employee_exclusions` (przy eksporcie faktycznie filtrują).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Download, Save, Upload } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { MultiSelect } from '@/components/MultiSelect';
import { Select } from '@/components/Select';
import { Tabs, type TabsItem } from '@/components/Tabs';
import { useToast } from '@/contexts/ToastContext';
import { useDictionary } from '@/hooks/useDictionary';
import { api } from '@/lib/api';

import { EmployeeBidirectionalSyncModal } from './EmployeeBidirectionalSyncModal';
import { ExportEmployeesModal } from './ExportEmployeesModal';
import { type ErpIntegrationDto } from './IntegrationsTab';
import { SyncEmployeesModal } from './SyncEmployeesModal';

const PREFIX = 'tenant.settings.integrations.employeeSync';

type ExclusionOption = { value: string; label: string };

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('pl-PL');
}

export function EmployeeSyncPanel({ integration }: { integration: ErpIntegrationDto }) {
  const { t } = useTranslation();

  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [bidirectionalOpen, setBidirectionalOpen] = useState(false);

  // Dwukierunkowa synchronizacja jest FK-only (jak u kontrahentów). Panel pracowników i tak jest
  // pokazywany tylko dla Symfonia FK, ale bramkujemy jawnie na wypadek zmian.
  const isFk = integration.erpSystem === 'symfonia_fk';

  // Kontrolki „do decyzji / bez logiki" — stan lokalny, nic jeszcze nie zapisujemy.
  const [matchKey, setMatchKey] = useState('nip');
  const [autoSync, setAutoSync] = useState(false);
  const [interval, setIntervalValue] = useState('60');
  const [direction, setDirection] = useState('import');

  // Źródła opcji dla wykluczeń — grupy ze słownika pracowników, lista pracowników firmy.
  const groupsDict = useDictionary('employee_groups');
  const groupOptions = groupsDict.activeItems.map((item) => ({
    value: item.code,
    label: item.label,
  }));

  const employeesQ = useQuery({
    queryKey: ['employee-sync-exclusions', 'employees', integration.companyId],
    queryFn: async () => {
      const res = await api.get<{
        employees: Array<{ id: string; firstName: string; lastName: string }>;
      }>('/employees', { params: { companyId: integration.companyId, limit: 200 } });
      return res.data.employees ?? [];
    },
    staleTime: 60_000,
  });
  const employeeOptions = (employeesQ.data ?? []).map((e) => ({
    value: e.id,
    label: `${e.firstName} ${e.lastName}`.trim(),
  }));

  const exclusionProps = {
    integrationId: integration.id,
    groupOptions,
    employeeOptions,
    // Źródło opcji jeszcze się wczytuje → pole wyłączone (zamiast mylącego „Brak wyników”).
    loadingOptions: { groups: groupsDict.isLoading, employees: employeesQ.isLoading },
  };

  const importTab = (
    <div className="space-y-4">
      <section>
        <p className="mb-2 text-xs text-text-tertiary">{t(`${PREFIX}.importTab.hint`)}</p>
        <Button
          variant="primary"
          onClick={() => setImportOpen(true)}
          data-testid="employee-sync-import"
        >
          <Download className="h-4 w-4" />
          {t(`${PREFIX}.importOnly`)}
        </Button>
      </section>
      <ExclusionsSection
        {...exclusionProps}
        idPrefix="import"
        scopeLabel={t(`${PREFIX}.tabs.import`)}
      />
    </div>
  );

  const exportTab = (
    <div className="space-y-4">
      <section>
        <p className="mb-2 text-xs text-text-tertiary">{t(`${PREFIX}.exportTab.hint`)}</p>
        <Button
          variant="primary"
          onClick={() => setExportOpen(true)}
          data-testid="employee-sync-export"
        >
          <Upload className="h-4 w-4" />
          {t(`${PREFIX}.exportOnly`)}
        </Button>
      </section>
      <ExclusionsSection
        {...exclusionProps}
        idPrefix="export"
        scopeLabel={t(`${PREFIX}.tabs.export`)}
      />
    </div>
  );

  const automationTab = (
    <div className="space-y-4">
      {/* ── Dwukierunkowo „obie strony" (FK, silnik konfliktów §4a) — akcja manualna ── */}
      {isFk && (
        <section className="rounded-lg border border-accent-500/30 bg-accent-500/5 p-4">
          <div className="mb-1 flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4 text-accent-500" aria-hidden="true" />
            <h4 className="text-sm font-semibold text-text-primary">
              {t(`${PREFIX}.bidir.title`)}
            </h4>
          </div>
          <p className="mb-3 text-xs text-text-tertiary">{t(`${PREFIX}.bidir.hint`)}</p>
          <Button
            variant="primary"
            onClick={() => setBidirectionalOpen(true)}
            data-testid="employee-sync-bidirectional"
          >
            <ArrowLeftRight className="h-4 w-4" />
            {t(`${PREFIX}.bidir.button`)}
          </Button>
        </section>
      )}
      <section className="rounded-lg border border-dashed border-border-secondary p-4">
        <div className="mb-1 flex items-center justify-between gap-3">
          <h4 className="text-sm font-semibold text-text-primary">{t(`${PREFIX}.auto.title`)}</h4>
          <span className="rounded-full bg-bg-tertiary px-2 py-0.5 text-[0.68rem] font-medium uppercase tracking-wide text-text-tertiary">
            {t(`${PREFIX}.auto.soonBadge`)}
          </span>
        </div>
        <Checkbox
          label={t(`${PREFIX}.auto.enable`)}
          checked={autoSync}
          onChange={(e) => setAutoSync(e.target.checked)}
        />
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label={t(`${PREFIX}.auto.interval`)}
            value={interval}
            onChange={(e) => setIntervalValue(e.target.value)}
            disabled={!autoSync}
            options={[
              { value: '15', label: t(`${PREFIX}.auto.int15`) },
              { value: '60', label: t(`${PREFIX}.auto.int60`) },
              { value: '360', label: t(`${PREFIX}.auto.int360`) },
              { value: '1440', label: t(`${PREFIX}.auto.int1440`) },
            ]}
          />
          <Select
            label={t(`${PREFIX}.auto.direction`)}
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
            disabled={!autoSync}
            options={[
              { value: 'import', label: t(`${PREFIX}.dir.import`) },
              { value: 'export', label: t(`${PREFIX}.dir.export`) },
              ...(isFk ? [{ value: 'both', label: t(`${PREFIX}.dir.both`) }] : []),
            ]}
          />
        </div>
        <p className="mt-1.5 text-[0.68rem] text-text-tertiary">
          {t(`${PREFIX}.auto.entitiesHint`)}
        </p>
      </section>
      <ExclusionsSection
        {...exclusionProps}
        idPrefix="automation"
        scopeLabel={t(`${PREFIX}.tabs.automation`)}
      />
    </div>
  );

  const tabs: TabsItem[] = [
    { value: 'import', label: t(`${PREFIX}.tabs.import`), content: importTab },
    { value: 'export', label: t(`${PREFIX}.tabs.export`), content: exportTab },
    { value: 'automation', label: t(`${PREFIX}.tabs.automation`), content: automationTab },
  ];

  return (
    <div className="space-y-6">
      {/* ── Statystyki (wspólne) ───────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label={t(`${PREFIX}.stat.mapped`)} value="—" />
        <Stat label={t(`${PREFIX}.stat.lastSync`)} value={formatDateTime(integration.lastSyncAt)} />
        <Stat
          label={t(`${PREFIX}.stat.status`)}
          badge={integration.isActive ? 'active' : 'inactive'}
          badgeText={
            integration.isActive
              ? t('tenant.settings.integrations.list.statusActive')
              : t('tenant.settings.integrations.list.statusInactive')
          }
        />
      </div>

      {/* ── Klucz dopasowania (wspólny dla importu i eksportu) ──── */}
      <section className="rounded-lg border border-border-primary bg-bg-secondary p-4">
        <Select
          label={t(`${PREFIX}.matchKey.label`)}
          value={matchKey}
          onChange={(e) => setMatchKey(e.target.value)}
          options={[
            { value: 'nip', label: t(`${PREFIX}.matchKey.nip`) },
            { value: 'code', label: t(`${PREFIX}.matchKey.code`) },
            { value: 'pesel', label: t(`${PREFIX}.matchKey.pesel`) },
          ]}
          className="max-w-xs"
        />
        <p className="mt-2 text-xs text-text-tertiary">{t(`${PREFIX}.matchKey.hint`)}</p>
      </section>

      {/* ── Zakładki: Import / Eksport / Automatyzacja ─────────── */}
      <Tabs idPrefix="employee-sync" items={tabs} />

      <SyncEmployeesModal
        open={importOpen}
        integrationId={integration.id}
        integrationName={integration.name}
        onClose={() => setImportOpen(false)}
      />
      <ExportEmployeesModal
        open={exportOpen}
        integrationId={integration.id}
        integrationName={integration.name}
        onClose={() => setExportOpen(false)}
      />
      <EmployeeBidirectionalSyncModal
        open={bidirectionalOpen}
        integrationId={integration.id}
        integrationName={integration.name}
        onClose={() => setBidirectionalOpen(false)}
      />
    </div>
  );
}

interface ExclusionRuleSet {
  groups: string[];
  employees: string[];
}
type DirectionExclusions = Partial<Record<'import' | 'export' | 'automation', ExclusionRuleSet>>;

/**
 * Wykluczenia z synchronizacji pracowników dla jednego kierunku. Ładuje i zapisuje reguły do
 * `erp_integrations.employee_exclusions` (per kierunek). Filtrowanie działa dziś przy EKSPORCIE (my→FK);
 * dla importu/automatu reguły są zapisywane, ale jeszcze nie stosowane.
 */
function ExclusionsSection({
  integrationId,
  groupOptions,
  employeeOptions,
  loadingOptions,
  idPrefix,
  scopeLabel,
}: {
  integrationId: string;
  groupOptions: ExclusionOption[];
  employeeOptions: ExclusionOption[];
  loadingOptions: { groups: boolean; employees: boolean };
  idPrefix: string;
  scopeLabel: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [skipGroups, setSkipGroups] = useState<string[]>([]);
  const [skipEmployees, setSkipEmployees] = useState<string[]>([]);

  const dataQ = useQuery({
    queryKey: ['erp-employee-exclusions', integrationId],
    queryFn: async () => {
      const res = await api.get<{ exclusions: DirectionExclusions }>(
        `/erp-integrations/${integrationId}/employee-exclusions`,
      );
      return res.data.exclusions ?? {};
    },
  });

  useEffect(() => {
    const rules = dataQ.data?.[idPrefix as 'import' | 'export' | 'automation'];
    setSkipGroups(rules?.groups ?? []);
    setSkipEmployees(rules?.employees ?? []);
  }, [dataQ.data, idPrefix]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const all: DirectionExclusions = {
        ...(dataQ.data ?? {}),
        [idPrefix]: { groups: skipGroups, employees: skipEmployees },
      };
      await api.put(`/erp-integrations/${integrationId}/employee-exclusions`, { exclusions: all });
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t(`${PREFIX}.exclusions.saved`) });
      queryClient.invalidateQueries({ queryKey: ['erp-employee-exclusions', integrationId] });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t(`${PREFIX}.exclusions.saveError`) });
    },
  });

  return (
    <section className="rounded-lg border border-dashed border-border-secondary p-4">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold text-text-primary">
          {t(`${PREFIX}.exclusions.title`)}
          <span className="ml-1.5 font-normal text-text-tertiary">· {scopeLabel}</span>
        </h4>
      </div>
      <p className="mb-3 text-xs text-text-tertiary">
        {idPrefix === 'automation'
          ? t(`${PREFIX}.exclusions.automationHint`)
          : t(`${PREFIX}.exclusions.hint`)}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MultiSelect
          label={t(`${PREFIX}.exclusions.groups`)}
          value={skipGroups}
          onChange={setSkipGroups}
          options={groupOptions}
          disabled={dataQ.isLoading || loadingOptions.groups}
          placeholder={t(`${PREFIX}.exclusions.groupsPlaceholder`)}
          data-testid={`employee-sync-skip-groups-${idPrefix}`}
        />
        <MultiSelect
          label={t(`${PREFIX}.exclusions.employees`)}
          value={skipEmployees}
          onChange={setSkipEmployees}
          options={employeeOptions}
          disabled={dataQ.isLoading || loadingOptions.employees}
          placeholder={t(`${PREFIX}.exclusions.employeesPlaceholder`)}
          data-testid={`employee-sync-skip-employees-${idPrefix}`}
        />
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          size="sm"
          leftIcon={<Save className="h-4 w-4" />}
          loading={saveMutation.isPending}
          // Bez wczytanych reguł zapis nadpisałby pozostałe zakładki pustymi listami.
          disabled={dataQ.data === undefined}
          onClick={() => saveMutation.mutate()}
          data-testid={`employee-sync-save-exclusions-${idPrefix}`}
        >
          {t(`${PREFIX}.exclusions.save`)}
        </Button>
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  badge,
  badgeText,
}: {
  label: string;
  value?: string;
  badge?: 'active' | 'inactive';
  badgeText?: string;
}) {
  return (
    <div className="rounded-lg border border-border-primary bg-bg-secondary p-4">
      <div className="text-[0.68rem] font-medium uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      {badge ? (
        <span
          className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
            badge === 'active'
              ? 'bg-green-500/10 text-green-700 dark:text-green-400'
              : 'bg-bg-tertiary text-text-tertiary'
          }`}
        >
          {badgeText}
        </span>
      ) : (
        <div className="mt-1 text-lg font-semibold text-text-primary">{value}</div>
      )}
    </div>
  );
}
